import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, X } from 'lucide-react';
import {
  ACCOUNT_CLAIM_QUERY_KEY,
  CLAIM_PAGE_PATH,
  ClaimUnavailableError,
  startSelfClaim,
} from './accountClaim';

const INVITE = 'Make it yours: sign in with Google or set your own password, so LineSide can reach your inbox.';
const REPLACES = 'The sign-in you choose replaces the username and password you were given. With Google, there’s no separate LineSide password.';

/** Starts a self-claim and hands off to the claim page. */
function useMakeItYours() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const start = async () => {
    setPending(true);
    setError('');
    try {
      await startSelfClaim();
      navigate(CLAIM_PAGE_PATH);
    } catch (failure) {
      setError(failure.message);
      setPending(false);
      // A refusal may mean the account was claimed meanwhile.
      if (failure instanceof ClaimUnavailableError) queryClient.invalidateQueries({ queryKey: ACCOUNT_CLAIM_QUERY_KEY });
    }
  };
  return { start, pending, error };
}

function StartButton({ claim, className }) {
  return (
    <button type="button" onClick={claim.start} disabled={claim.pending} className={className}>
      <KeyRound size={15} aria-hidden="true" />
      {claim.pending ? 'Opening…' : 'Make it yours'}
    </button>
  );
}

const primary = 'inline-flex min-h-9 shrink-0 items-center gap-2 rounded-lg bg-cyan-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-cyan-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600 focus-visible:ring-offset-2 disabled:opacity-60';

/**
 * Above every page while the account is unclaimed. Dismissing hides it until the
 * next page load or sign-in: the owner keeps that in memory only.
 */
export function MakeItYoursBanner({ onDismiss }) {
  const claim = useMakeItYours();
  return (
    <section aria-labelledby="make-it-yours-banner" className="border-b border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-slate-700 sm:px-6">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 basis-72">
          <h2 id="make-it-yours-banner" className="font-semibold text-slate-900">This account was set up for you</h2>
          <p className="mt-0.5">{INVITE} {REPLACES}</p>
          {claim.error && <p role="alert" className="mt-1 text-red-800">{claim.error}</p>}
        </div>
        <StartButton claim={claim} className={primary} />
        <button type="button" onClick={onDismiss} aria-label="Dismiss for now"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-cyan-100 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600">
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

/** The same invitation on the Account page, for an unclaimed account. */
export function MakeItYoursSection({ username }) {
  const claim = useMakeItYours();
  return (
    <section aria-labelledby="make-it-yours-heading" className="mb-6 rounded-xl border border-cyan-200 bg-cyan-50 p-5 text-sm text-slate-700">
      <h2 id="make-it-yours-heading" className="text-lg font-medium text-gray-900">Make this account yours</h2>
      <p className="mt-1">This account was set up for you{username ? <> and signs in as <span className="break-all font-mono">{username}</span></> : null}. {INVITE}</p>
      <p className="mt-1">{REPLACES}</p>
      {claim.error && <p role="alert" className="mt-2 text-red-800">{claim.error}</p>}
      <div className="mt-4"><StartButton claim={claim} className={primary} /></div>
    </section>
  );
}
